import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, CircularProgress } from '@material-ui/core';

import { WarehouseInventoryV2Api } from 'api/warehouseInventoryV2';
import { UserContext } from 'contexts/UserContext';
import { DataTable, Notifications } from 'shared';

import { ActionModal } from './ActionModal';
import { ActionsCell } from './ActionsCell';
import { EmptyPalletCreator } from './emptyPalletCreator';
import { getInventorySearchValuesKey } from './inventorySearchValues';
import { ViewPalletsModal } from './ViewPalletsModal';
import { AdjustCasesAndLooseUnitsAction } from './actions/adjustCasesAndLooseUnitsAction';
import { BulkMovePalletsAction } from './actions/bulkMovePallets/BulkMovePalletsAction';
import { EntireLocationEmptyAction } from './actions/entireLocationEmptyAction';
import { LotNotInLocationAction } from './actions/lotNotInLocationAction';
import { MoveCasesAndLooseUnitsFromKnownOriginAction } from './actions/moveCasesAndLooseUnitsAction';

import './locationInventoryV2Styles.css';

const warehouseInventoryV2Api = new WarehouseInventoryV2Api();

function LotTabExpansion({
    lotActive,
    onLinkClicked,
    onSuccess,
    parentRow,
    searchValues: parentSearchValues,
    selectedLotId,
}) {
    const [notification, setNotification] = useState({ text: null, level: null });
    const [rows, setRows] = useState([]);
    const [viewActions, setViewActions] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});
    const [actions, setActions] = useState([]);
    const [locationsLoading, setLocationsLoading] = useState(false);
    const [selectedLocationId, setSelectedLocationId] = useState(null);
    const [selectedLocationName, setSelectedLocationName] = useState('');
    const { roles, user } = useContext(UserContext);
    const isMounted = useRef(true);
    const fetchGenerationRef = useRef(0);
    const userCanOffsite = roles?.some((role) => role.name === 'OFFSITE_TRANSFERS');
    const [locationCanBulkMove, setLocationCanBulkMove] = useState(false);
    const parentSearchValuesKey = useMemo(() => getInventorySearchValuesKey(parentSearchValues), [parentSearchValues]);

    useEffect(
        () => () => {
            isMounted.current = false;
        },
        [],
    );

    useEffect(() => {
        getLocationDataForLot();

        return () => {
            fetchGenerationRef.current += 1;
        };
    }, [selectedLotId, parentSearchValuesKey]);

    const handleMovePalletSuccess = () => {
        getLocationDataForLot();
    };

    useEffect(() => {
        if (userCanOffsite && selectedRow?.enableBulkMove) {
            setLocationCanBulkMove(true);
        } else {
            setLocationCanBulkMove(false);
        }
    }, [selectedRow, userCanOffsite]);

    useEffect(() => {
        setActions([...baseActionDefinitions, ...(locationCanBulkMove ? [bulkMoveAction] : [])]);
    }, [locationCanBulkMove, selectedRow]);

    const showPalletModal = async (row) => {
        setSelectedLocationName(row.locationName);
        setSelectedLocationId(row.locationId);
    };

    const columns = [
        {
            Header: 'Location',
            accessor: 'locationName',
            Cell: ({ row }) => (
                <Button
                    variant="text"
                    color="primary"
                    onClick={() => handleLocationLinkClick(row.original.locationName)}
                >
                    {row.original.locationName}
                </Button>
            ),
        },
        {
            Header: 'Pallets',
            accessor: 'pallets',
            Cell: ({ row }) => {
                const numPallets = row.original.pallets;

                return (
                    <Button
                        disabled={!numPallets}
                        onClick={() => {
                            showPalletModal(row.original);
                        }}
                    >
                        {!numPallets ? <span>0</span> : <strong>{numPallets}</strong>}
                    </Button>
                );
            },
        },
        { Header: 'Cases', accessor: 'cases' },
        { Header: 'Loose Units', accessor: 'looseUnits' },
        { Header: 'Total Units', accessor: 'totalUnits' },
        {
            Header: 'Actions',
            Cell: ({ row }) => (
                <ActionsCell row={row} setSelectedRow={setSelectedRow} setViewActions={setViewActions} />
            ),
        },
    ];

    const handleLocationLinkClick = (locationName) => {
        onLinkClicked({ tabIndex: 1, searchBy: 'locationName', searchTerm: locationName });
    };

    const handleModalClose = () => {
        setViewActions(false);
        setSelectedRow({});
    };

    const handleViewPalletModalClose = () => {
        setSelectedLocationId('');
        setSelectedLocationName('');
    };

    const handleSuccess = (message) => {
        handleModalClose();
        getLocationDataForLot();
        onSuccess(message);
    };

    // API call to GET data
    const getLocationDataForLot = async () => {
        if (!selectedLotId) {
            return;
        }

        const generation = fetchGenerationRef.current + 1;
        fetchGenerationRef.current = generation;
        setLocationsLoading(true);

        try {
            const searchValues = [{ searchBy: 'lotId', searchTerm: selectedLotId }];
            const result = await warehouseInventoryV2Api.getLocations(user.facilityID, [
                ...parentSearchValues,
                ...searchValues,
            ]);

            if (!isMounted.current || fetchGenerationRef.current !== generation) {
                return;
            }

            if (result.status) {
                setRows(
                    result.data.locations.map((location) => ({
                        locationId: location.locationId,
                        locationName: location.locationName,
                        active: location.numLots > 0,
                        uniqueSkus: location.numSku,
                        lots: location.numLots,
                        pallets: location.numPallets,
                        cases: location.numCases,
                        looseUnits: location.numLooseUnits,
                        totalUnits: location.numTotalUnits,
                        locInvType: location.inventoryType,
                        enableBulkMove: location.enableBulkMove ?? false,
                    })),
                );
            } else {
                setNotification({ text: result.msg, level: 'error' });
            }
        } finally {
            if (isMounted.current && fetchGenerationRef.current === generation) {
                setLocationsLoading(false);
            }
        }
    };

    // Action Definitions
    const baseActionDefinitions = [
        {
            text: 'Move Cases and Loose Units',
            value: 'moveCaseAndLooseUnits',
            componentJSX() {
                return (
                    <MoveCasesAndLooseUnitsFromKnownOriginAction
                        lotId={selectedLotId}
                        locationName={selectedRow?.locationName}
                        locationId={selectedRow?.locationId}
                        locInvType={selectedRow?.locInvType}
                        numPallets={selectedRow?.pallets}
                        onSuccess={handleSuccess}
                        cases={selectedRow?.cases}
                        units={selectedRow?.looseUnits}
                        unitsPerCase={parentRow.original?.unitsPerCase}
                    />
                );
            },
        },
        {
            text: 'Lot Not in Location',
            value: 'lotNotInLocation',
            componentJSX() {
                return (
                    <LotNotInLocationAction
                        lotId={selectedLotId}
                        locationId={selectedRow?.locationId}
                        locInvType={selectedRow?.locInvType}
                        onSuccess={handleSuccess}
                        onCancel={handleModalClose}
                    />
                );
            },
        },
        {
            text: 'Entire Location Empty',
            value: 'entireLocationEmpty',
            componentJSX() {
                return (
                    <EntireLocationEmptyAction
                        locationId={selectedRow?.locationId}
                        onSuccess={handleSuccess}
                        onCancel={handleModalClose}
                    />
                );
            },
        },
        {
            text: 'Create Empty Pallet',
            value: 'createEmptyPallet',
            componentJSX() {
                return <EmptyPalletCreator locationId={selectedRow?.locationId} />;
            },
        },
        {
            text: 'Adjust Cases and Loose Units',
            value: 'adjustCasesAndLooseUnits',
            componentJSX() {
                return (
                    <AdjustCasesAndLooseUnitsAction
                        locationId={selectedRow?.locationId}
                        lotId={selectedLotId}
                        caseNum={selectedRow?.cases}
                        looseUnitNum={selectedRow?.looseUnits}
                        onSuccess={handleSuccess}
                        onCancel={handleModalClose}
                        locInvType={selectedRow?.locInvType}
                        palletNum={selectedRow?.pallets}
                        unitsPerCase={parentRow.original?.unitsPerCase}
                    />
                );
            },
        },
    ];

    const bulkMoveAction = {
        text: 'Bulk Move Pallets',
        value: 'bulkMovePallets',
        componentJSX() {
            return (
                <BulkMovePalletsAction
                    locationId={selectedRow?.locationId}
                    locationName={selectedRow?.locationName}
                    onSuccess={handleSuccess}
                    onCancel={handleModalClose}
                    lotId={selectedLotId}
                />
            );
        },
    };

    return (
        lotActive && (
            <div className="expanded-table-div">
                <Notifications options={notification} />
                {locationsLoading && <CircularProgress color="inherit" size={48} />}
                {!locationsLoading && (
                    <DataTable columns={columns} data={rows} editable={false} tableContainerMaxHeight={600} />
                )}
                <ActionModal
                    title={`Actions for Location ${selectedRow.locationName}`}
                    open={viewActions}
                    row={selectedRow}
                    actions={actions}
                    closeModal={handleModalClose}
                />
                <ViewPalletsModal
                    open={selectedLocationId > 0}
                    closeModal={handleViewPalletModalClose}
                    lotId={selectedLotId}
                    locationName={selectedLocationName}
                    locationId={selectedLocationId}
                    setNotification={setNotification}
                    onMovePalletSuccess={handleMovePalletSuccess}
                />
            </div>
        )
    );
}

export { LotTabExpansion };
